import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, FileText, Calendar, User, Search, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/auth";
import type { Project } from "@shared/schema";
import { cn } from "@/lib/utils";

export function ProjectLoggingSettings() {
  const { toast } = useToast();
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [exportFormat, setExportFormat] = useState<"jsonl" | "txt">("jsonl");

  // Fetch all projects
  const { data: projects = [], isLoading } = useQuery<Project[]>({
    queryKey: ['/api/projects'],
  });

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return projects;
    
    const query = searchQuery.toLowerCase();
    return projects.filter((project) => 
      project.id.toLowerCase().includes(query) ||
      project.projectName?.toLowerCase().includes(query)
    );
  }, [projects, searchQuery]);

  const handleDownloadLogs = async () => {
    if (!selectedProject) {
      toast({
        title: "No project selected",
        description: "Please select a project to download audit logs",
        variant: "destructive",
      });
      return;
    }

    setIsDownloading(true);
    try {
      // Use authFetch to include JWT token
      const response = await authFetch(`/api/projects/${selectedProject}/audit-log?format=${exportFormat}`);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        }
        throw new Error('Failed to download audit logs');
      }

      // Get the blob data
      const blob = await response.blob();
      
      // Check if the blob is empty (no logs)
      if (blob.size === 0) {
        toast({
          title: "No audit logs found",
          description: `Project ${selectedProject} has no audit logs yet. Logs are created when you make changes to the project, pulses, or tasks.`,
        });
        return;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedProject}_audit_log.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Download complete",
        description: `Audit logs for project ${selectedProject} downloaded successfully`,
      });
    } catch (error) {
      console.error("Error downloading audit logs:", error);
      toast({
        title: "Download failed",
        description: error instanceof Error ? error.message : "Failed to download audit logs",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Card className="navy-card border-gray-600">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Project Audit Logs
        </CardTitle>
        <CardDescription className="text-gray-400">
          Download comprehensive audit logs for business analytics, client reporting, and compliance tracking
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* What is logged section */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-300">What's Logged:</h4>
          <ul className="text-sm text-gray-400 space-y-1 pl-4">
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Project creation, updates, status changes</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Pulse creation, updates, and deletions</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Task creation, updates, completions, and deletions</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Complete before/after snapshots for all changes</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Timestamps and user information for every action</span>
            </li>
          </ul>
        </div>

        {/* Project selection and download */}
        <div className="space-y-4">
          {/* Search and select combined */}
          <div className="space-y-2">
            <Label htmlFor="project-search" className="text-gray-300">
              Search and Select Project
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
              <Input
                id="project-search"
                type="text"
                placeholder="Search by project ID or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="navy-input border-gray-600 text-white pl-10"
                data-testid="input-search-projects"
              />
            </div>
            {searchQuery && (
              <p className="text-xs text-gray-400">
                Found {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Filtered results list */}
          {searchQuery && (
            <div className="max-h-[200px] overflow-y-auto border border-gray-600 rounded-md navy-card">
              {filteredProjects.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-gray-400">
                  No projects found matching "{searchQuery}"
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {filteredProjects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => {
                        setSelectedProject(project.id);
                        setSearchQuery("");
                      }}
                      className={cn(
                        "w-full px-3 py-2 text-left text-sm hover:bg-gray-700 transition-colors flex items-center justify-between",
                        selectedProject === project.id && "bg-gray-700"
                      )}
                      data-testid={`button-select-project-${project.id}`}
                    >
                      <span className="text-white">
                        {project.id} - {project.projectName}
                      </span>
                      {selectedProject === project.id && (
                        <Check className="h-4 w-4 text-green-400" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selected project display */}
          {selectedProject && (
            <div className="p-3 bg-gray-800/50 border border-gray-600 rounded-md">
              <div className="text-xs text-gray-400 mb-1">Selected Project:</div>
              <div className="text-sm text-white font-medium flex items-center justify-between">
                <span>{selectedProject}</span>
                <button
                  onClick={() => setSelectedProject("")}
                  className="text-xs text-red-400 hover:text-red-300"
                  data-testid="button-clear-selection"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Format selector */}
          <div className="space-y-2">
            <Label className="text-gray-300">Export Format</Label>
            <div className="flex gap-3">
              <button
                onClick={() => setExportFormat("jsonl")}
                className={cn(
                  "flex-1 px-4 py-2 rounded-md border text-sm transition-colors",
                  exportFormat === "jsonl"
                    ? "bg-blue-600 border-blue-500 text-white"
                    : "bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700"
                )}
                data-testid="button-format-jsonl"
              >
                <div className="flex items-center justify-center gap-2">
                  {exportFormat === "jsonl" && <Check className="h-4 w-4" />}
                  <span>JSONL (Data)</span>
                </div>
              </button>
              <button
                onClick={() => setExportFormat("txt")}
                className={cn(
                  "flex-1 px-4 py-2 rounded-md border text-sm transition-colors",
                  exportFormat === "txt"
                    ? "bg-blue-600 border-blue-500 text-white"
                    : "bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700"
                )}
                data-testid="button-format-txt"
              >
                <div className="flex items-center justify-center gap-2">
                  {exportFormat === "txt" && <Check className="h-4 w-4" />}
                  <span>TXT (Readable)</span>
                </div>
              </button>
            </div>
          </div>

          <Button
            onClick={handleDownloadLogs}
            disabled={!selectedProject || isDownloading}
            className="w-full"
            data-testid="button-download-audit-log"
          >
            <Download className="h-4 w-4 mr-2" />
            {isDownloading ? "Downloading..." : "Download Audit Log"}
          </Button>
        </div>

        {/* Format information */}
        <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-600 space-y-2">
          <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Export Format
          </h4>
          {exportFormat === "jsonl" ? (
            <p className="text-xs text-gray-400">
              <strong className="text-gray-300">JSON Lines format</strong> (.jsonl): Each line is a complete JSON object 
              representing one audit event with full before/after state snapshots. Ideal for importing into analytics 
              tools like Excel, Tableau, or custom data pipelines.
            </p>
          ) : (
            <p className="text-xs text-gray-400">
              <strong className="text-gray-300">Human-readable text format</strong> (.txt): Single-line entries formatted 
              as "Mo, 24.01 - 14:12: Action description - Username". Perfect for quick review, client reports, 
              and compliance documentation.
            </p>
          )}
        </div>

        {/* Example entry */}
        <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-600 space-y-2">
          <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
            <User className="h-4 w-4" />
            Example Log Entry
          </h4>
          {exportFormat === "jsonl" ? (
            <pre className="text-xs text-gray-400 overflow-x-auto">
{`{
  "id": 123,
  "projectId": "ABC1234",
  "timestamp": "2025-10-22T11:45:00Z",
  "userId": 5,
  "actionType": "task_completed",
  "entityType": "task",
  "entityId": "789",
  "beforeState": {"status": "working"},
  "afterState": {"status": "done"},
  "metadata": {"pulseName": "Sprint 3"}
}`}
            </pre>
          ) : (
            <pre className="text-xs text-gray-400 overflow-x-auto">
{`Mo, 22.10 - 11:45: Task "Review documentation" completed - John
Mo, 22.10 - 14:30: Pulse "Sprint 3" status changed: in_progress → done - Sarah
Di, 23.10 - 09:15: Time entry added: 120 minutes - Michael`}
            </pre>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
